type CloudOpts = {
  targetDiameter?: number;
  minBumpsPerSide?: number;
};

/**
 * Build an SVG path `d` string for a revision cloud drawn around a
 * w × h bounding box (in px).
 *
 * The cloud is formed by a series of small outward-bumping semicircular arcs
 * along each edge, tracing a clockwise path starting at (0, 0).
 *
 * Arc radii are derived from the step size in each direction so every arc
 * is a proper semicircle — this gives the classic engineering revision-cloud
 * appearance.
 *
 * Pass `opts.targetDiameter` to hint the desired arc diameter; the actual
 * radii are quantised to fit whole arcs along each edge.
 * Pass `opts.minBumpsPerSide` to ensure a minimum number of bumps per edge.
 */
export function buildRevisionCloudPathData(
  w: number,
  h: number,
  opts?: CloudOpts,
): string {
  const hintR = (opts?.targetDiameter ?? 24) / 2;
  const minBumps = opts?.minBumpsPerSide ?? 2;

  // Compute the number of arcs per edge so each arc is as close to hintR as
  // possible while fitting exactly (no partial arcs).
  const numX = Math.max(minBumps, Math.round(w / (2 * hintR)));
  const numY = Math.max(minBumps, Math.round(h / (2 * hintR)));

  // Actual step sizes — arcs are semicircles so radius = half step.
  const stepX = w / numX;
  const stepY = h / numY;
  const rx = stepX / 2; // SVG arc radius along top/bottom edges
  const ry = stepY / 2; // SVG arc radius along left/right edges

  // sweep-flag=1 → clockwise in SVG coords (y-down) → bumps outward
  // for a clockwise perimeter traversal.
  const parts: string[] = [`M 0 0`];

  // Top edge: left → right, bumps upward
  for (let i = 0; i < numX; i++) {
    parts.push(`a ${rx} ${rx} 0 0 1 ${stepX} 0`);
  }

  // Right edge: top → bottom, bumps rightward
  for (let i = 0; i < numY; i++) {
    parts.push(`a ${ry} ${ry} 0 0 1 0 ${stepY}`);
  }

  // Bottom edge: right → left, bumps downward
  for (let i = 0; i < numX; i++) {
    parts.push(`a ${rx} ${rx} 0 0 1 ${-stepX} 0`);
  }

  // Left edge: bottom → top, bumps leftward
  for (let i = 0; i < numY; i++) {
    parts.push(`a ${ry} ${ry} 0 0 1 0 ${-stepY}`);
  }

  parts.push("Z");
  return parts.join(" ");
}
